import { useCallback, useEffect, useRef, useState } from "react";

export type PresenceState = "entering" | "exiting";

/** The minimal event shape needed to ignore animationend events bubbling up from child elements. */
interface PresenceAnimationEvent {
  readonly target: EventTarget;
  readonly currentTarget: EventTarget;
}

/** Exiting entries stay in the DOM until their animation ends, so they must still be rendered. */
export interface PresenceEntry<T> {
  readonly item: T;
  readonly key: number;
  readonly state: PresenceState;
  readonly onAnimationEnd: (event?: PresenceAnimationEvent) => void;
}

interface StoredEntry<T> {
  readonly item: T;
  readonly key: number;
  readonly state: PresenceState;
}

type PresenceItem<T> = T | null | false | undefined;

interface PresenceStore<T> {
  readonly source: PresenceItem<T>;
  readonly entries: StoredEntry<T>[];
  readonly nextKey: number;
}

const createStore = <T>(item: PresenceItem<T>): PresenceStore<T> => {
  if (!item) return { source: item, entries: [], nextKey: 0 };
  return { source: item, entries: [{ item, key: 0, state: "entering" }], nextKey: 1 };
};

const updateStore = <T>(store: PresenceStore<T>, item: PresenceItem<T>): PresenceStore<T> => {
  const exitingEntries = store.entries.map((entry): StoredEntry<T> => ({ ...entry, state: "exiting" }));
  if (!item) return { source: item, entries: exitingEntries, nextKey: store.nextKey };

  const revivedIndex = exitingEntries.findIndex((entry) => Object.is(entry.item, item));
  if (revivedIndex >= 0) {
    const entries = exitingEntries.map((entry, index): StoredEntry<T> => (index === revivedIndex ? { ...entry, state: "entering" } : entry));
    return { source: item, entries, nextKey: store.nextKey };
  }

  return {
    source: item,
    entries: [...exitingEntries, { item, key: store.nextKey, state: "entering" }],
    nextKey: store.nextKey + 1,
  };
};

/**
 * Keeps an item mounted while its CSS exit animation plays. A truthy item is "entering"; once it changes
 * or becomes falsy it is "exiting" until its `animationend` fires, or `durationMs` elapses, whichever is first.
 * @param item The current item, or a falsy value when nothing should be shown.
 * @param durationMs The exit animation duration, which must match the CSS.
 * @returns Every entry to render, including exiting ones. Render all of them, since an exiting and an entering item can overlap.
 */
export const usePresence = <T>(item: PresenceItem<T>, durationMs: number): PresenceEntry<T>[] => {
  const [store, setStore] = useState<PresenceStore<T>>(() => createStore(item));
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  // derived during render so the first frame already contains the entering entry
  let currentStore = store;
  if (!Object.is(store.source, item)) {
    currentStore = updateStore(store, item);
    setStore(currentStore);
  }

  const removeExiting = useCallback((key: number) => {
    const timer = timersRef.current.get(key);
    if (timer !== undefined) clearTimeout(timer);
    timersRef.current.delete(key);
    setStore((previous) => ({
      ...previous,
      entries: previous.entries.filter((entry) => entry.key !== key || entry.state !== "exiting"),
    }));
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    currentStore.entries.forEach(({ key, state }) => {
      const timer = timers.get(key);
      if (state === "exiting" && timer === undefined) {
        timers.set(
          key,
          setTimeout(() => removeExiting(key), durationMs),
        );
      } else if (state === "entering" && timer !== undefined) {
        clearTimeout(timer);
        timers.delete(key);
      }
    });
  }, [currentStore, durationMs, removeExiting]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return currentStore.entries.map(({ item: entryItem, key, state }) => ({
    item: entryItem,
    key,
    state,
    onAnimationEnd: (event) => {
      if (state !== "exiting") return;
      if (event && event.target !== event.currentTarget) return;
      removeExiting(key);
    },
  }));
};
